'use client'

import { useRef, useState } from 'react'
import { ShieldCheck, X } from 'lucide-react'
import {
  clientHasGpc,
  readClientConsent,
  writeClientConsent,
} from '@/lib/tracking/consent'
import { useFocusTrap } from '@/lib/hooks/useFocusTrap'

/**
 * "Your Privacy Choices" — the US opt-out surface (master plan §5.2): a
 * conspicuous control, zero-friction opt-out (no login, no verification), a
 * visible "request processed" confirmation (CA's 2026 regs), and automatic
 * Global Privacy Control honoring surfaced to the visitor. Also carries the
 * page-level tracking notice Meta's Business Tools Terms contractually require.
 * Opting out writes `ps_privacy=denied` and reloads so every tracker (Meta,
 * Google, Clarity, FirstPromoter) unloads — full suppression, not a
 * degraded-consent mode.
 */
export function PrivacyChoices() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-dark-muted hover:text-dark-text inline-flex min-h-11 items-center gap-1.5 text-xs transition-colors"
      >
        <ShieldCheck size={14} aria-hidden />
        Your Privacy Choices
      </button>
      {open && <PrivacyDialog onClose={() => setOpen(false)} />}
    </>
  )
}

function PrivacyDialog({ onClose }: { onClose: () => void }) {
  const [processed, setProcessed] = useState(false)
  const [consent, setConsent] = useState<'granted' | 'denied'>(() =>
    readClientConsent()
  )
  const [gpc] = useState<boolean>(() => clientHasGpc())
  const dialogRef = useRef<HTMLDivElement>(null)

  useFocusTrap(dialogRef)

  const optOut = (): void => {
    writeClientConsent('denied')
    setConsent('denied')
    setProcessed(true)
    window.setTimeout(() => window.location.reload(), 1500)
  }

  const optBackIn = (): void => {
    writeClientConsent('granted')
    setConsent('granted')
    setProcessed(true)
    window.setTimeout(() => window.location.reload(), 1500)
  }

  return (
    <div
      className="bg-ink/50 fixed inset-0 z-70 flex items-end justify-center p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-choices-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose()
        }}
        className="border-border bg-bg w-full max-w-md rounded-2xl border p-6 text-left shadow-xl focus:outline-none"
      >
        <div className="flex items-start justify-between gap-4">
          <h2
            id="privacy-choices-title"
            className="font-heading text-ink text-lg font-semibold"
          >
            Your Privacy Choices
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close privacy choices"
            className="text-hint hover:text-ink -m-2 flex h-11 w-11 items-center justify-center rounded-full transition-colors"
          >
            <X size={18} aria-hidden />
          </button>
        </div>

        <p className="text-body mt-3 text-sm leading-relaxed">
          PrepSolution uses cookies and similar technologies — including the
          Meta pixel and Google tags — to measure marketing and improve the
          site. Some of that data is shared with advertising partners. You can
          opt out of this sharing at any time; the site keeps working either
          way.
        </p>

        {gpc && (
          <p className="bg-success-light text-success mt-3 rounded-lg px-3 py-2 text-xs font-medium">
            Your browser is sending a Global Privacy Control signal — your
            opt-out request has been processed automatically.
          </p>
        )}

        {processed && (
          <p className="bg-success-light text-success mt-3 rounded-lg px-3 py-2 text-xs font-medium">
            Your request has been processed. Reloading…
          </p>
        )}

        {!processed && !gpc && (
          <div className="mt-5">
            {consent === 'granted' ? (
              <button
                type="button"
                onClick={optOut}
                className="bg-primary hover:bg-primary/90 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors"
              >
                Opt out of targeted advertising
              </button>
            ) : (
              <div>
                <p className="text-body text-sm">
                  You are currently opted out — no advertising or analytics
                  trackers load for you.
                </p>
                <button
                  type="button"
                  onClick={optBackIn}
                  className="border-border text-ink hover:bg-surface mt-3 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors"
                >
                  Opt back in
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
